import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { SystemNotificationService } from 'app/api/services';
import { JwtAuthService } from 'app/api/services/cookie-jwt-auth/jwt-auth.service';
import { SignalRService } from 'app/core/services/signalr.service';
import { SystemNotificationDto, NotificationAction } from 'app/api/models';

@Injectable({
    providedIn: 'root'
})
export class NotificationStackService {

    private readonly MAX_VISIBLE_NOTIFICATIONS = 5;
    private notificationsSubject = new BehaviorSubject<SystemNotificationDto[]>([]);
    public notifications$ = this.notificationsSubject.asObservable();
    private allUnreadNotifications: SystemNotificationDto[] = [];
    private shownNotificationIds: Set<number> = new Set();

    constructor(
        private notifService: SystemNotificationService,
        private jwtAuth: JwtAuthService,
        private router: Router,
        private signalRService: SignalRService
    ) {}

    /**
     * Check for new unread notifications and display them (max 5 at a time)
     */
    checkForNewNotifications(): void {
        const user = this.jwtAuth.getUser();
        if (!user?.id) return;

        this.notifService.apiSystemNotificationForUserGet$Json({
            userId: user.id
        }).subscribe(res => {
            if (!res?.data) return;

            // Filter for unread notifications
            this.allUnreadNotifications = res.data.filter(n => !n.read);

            // Show only the first 5 notifications
            this.updateVisibleNotifications();
        });
    }

    /**
     * Subscribe to SignalR for real-time notification push
     */
    subscribeToSignalR(): void {
        // Start SignalR connection
        this.signalRService.startConnection();

        // Subscribe to real-time notifications
        this.signalRService.notification$.subscribe(notification => {
            if (!notification) return;

            // Add to unread list if not already there
            const exists = this.allUnreadNotifications.find(
                n => n.systemNotificationId === notification.systemNotificationId
            );

            if (!exists) {
                this.allUnreadNotifications.unshift(notification); // Add to front
                this.updateVisibleNotifications(); // Show if slots available
            }
        });
    }

    /**
     * Update visible notifications (show max 5)
     */
    private updateVisibleNotifications(): void {
        // Get notifications that haven't been shown yet
        const notShownYet = this.allUnreadNotifications.filter(
            n => !this.shownNotificationIds.has(n.systemNotificationId)
        );

        // Get currently visible notifications
        const currentVisible = this.notificationsSubject.value;

        // Fill up to MAX_VISIBLE_NOTIFICATIONS
        const slotsAvailable = this.MAX_VISIBLE_NOTIFICATIONS - currentVisible.length;
        const toAdd = notShownYet.slice(0, slotsAvailable);

        // Mark these as shown
        toAdd.forEach(n => this.shownNotificationIds.add(n.systemNotificationId));

        // Update visible list
        const newVisible = [...currentVisible, ...toAdd];
        this.notificationsSubject.next(newVisible);
    }

    /**
     * Handle "View" button click - marks as read and shows next notification
     */
    handleView(notification: SystemNotificationDto): void {
        // Mark as read
        notification.read = true;
        this.notifService.apiSystemNotificationUpdateNotificationPut$Json({
            body: notification
        }).subscribe();

        // Remove from all lists
        this.removeNotification(notification);

        // Show next notification if available
        this.updateVisibleNotifications();

        // Navigate to relevant page based on notification type
        this.navigateToNotificationSource(notification);
    }

    /**
     * Navigate to the page that generated the notification
     */
    private navigateToNotificationSource(notification: SystemNotificationDto): void {
        // Determine navigation based on notification action
        switch (notification.action) {
            case NotificationAction.SupportTicket:
                // Support ticket notification - navigate to support tickets page
                // If targetId exists, we could use it to highlight/filter to specific ticket
                this.router.navigate(['/support-tickets']);
                break;

            case NotificationAction.TaskAdded:
            case NotificationAction.TaskCompleted:
            case NotificationAction.TaskCommented:
            case NotificationAction.TaskRemoved:
                // Task/swimlane notification - navigate to project management projects page
                // User can find the relevant project and task
                this.router.navigate(['/project-management/projects']);
                break;

            case NotificationAction.HolidayToApprove:
                // Holiday approval notification - navigate to ERP HR page
                this.router.navigate(['/erp/hr']);
                break;

            case NotificationAction.PurchaseOrderReview:
                // Purchase order notification - navigate to project management purchase orders page
                this.router.navigate(['/project-management/purchase-orders']);
                break;

            case undefined:
            case null:
            default:
                // No action or unknown notification type - go to dashboard
                this.router.navigate(['/dashboard']);
                break;
        }
    }

    /**
     * Dismiss a notification without marking as read (X button)
     */
    dismiss(notification: SystemNotificationDto): void {
        // Just remove from visible, but keep in allUnread list
        const current = this.notificationsSubject.value;
        const updated = current.filter(n => n.systemNotificationId !== notification.systemNotificationId);
        this.notificationsSubject.next(updated);

        // Show next notification if available
        this.updateVisibleNotifications();
    }

    /**
     * Remove notification from all tracking
     */
    private removeNotification(notification: SystemNotificationDto): void {
        // Remove from visible list
        const current = this.notificationsSubject.value;
        const updated = current.filter(n => n.systemNotificationId !== notification.systemNotificationId);
        this.notificationsSubject.next(updated);

        // Remove from all unread list
        this.allUnreadNotifications = this.allUnreadNotifications.filter(
            n => n.systemNotificationId !== notification.systemNotificationId
        );
    }

    /**
     * Clear all notifications from display
     */
    clearAll(): void {
        this.notificationsSubject.next([]);
        this.shownNotificationIds.clear();
    }
}
